/**
 * Step type definitions, field configurations, chip colors, and failure handling options.
 * This is the single source of truth for step schema used by both the web UI and
 * (conceptually) the extension sidebar.
 *
 * Data format matches the ExtensionScript.steps[] stored in the backend:
 *   { stepNumber, type: "action"|"wait"|"assertion", name, inputData: { action, selector, text, ... } }
 */

// ---------------------------------------------------------------------------
// Step templates keyed by composite type  (e.g. "action.navigate")
// ---------------------------------------------------------------------------

export const STEP_TEMPLATES = {
  'action.navigate': {
    label: 'Navigate',
    type: 'action',
    inputData: { action: 'navigate', url: '', timeout: 10000 },
    defaultName: 'Navigate to URL',
  },
  'action.type': {
    label: 'Type Text',
    type: 'action',
    inputData: { action: 'type', selector: '', text: '', clearFirst: true, timeout: 3000 },
    defaultName: 'Type into field',
  },
  'action.click': {
    label: 'Click Element',
    type: 'action',
    inputData: { action: 'click', selector: '', timeout: 3000 },
    defaultName: 'Click element',
  },
  'action.select': {
    label: 'Select Option',
    type: 'action',
    inputData: { action: 'select', selector: '', value: '', timeout: 3000 },
    defaultName: 'Select option',
  },
  'action.script': {
    label: 'Run Script',
    type: 'action',
    inputData: { action: 'script', script: '', timeout: 10000 },
    defaultName: 'Execute script',
  },
  'wait.selector': {
    label: 'Wait for Selector',
    type: 'wait',
    inputData: { waitType: 'selector', selector: '', timeout: 10000 },
    defaultName: 'Wait for element',
  },
  'wait.navigation': {
    label: 'Wait for Navigation',
    type: 'wait',
    inputData: { waitType: 'navigation', url: '', timeout: 15000 },
    defaultName: 'Wait for navigation',
  },
  'wait.time': {
    label: 'Wait (Delay)',
    type: 'wait',
    inputData: { waitType: 'time', timeout: 2000 },
    defaultName: 'Wait for delay',
  },
  'assertion.url': {
    label: 'Assert URL',
    type: 'assertion',
    inputData: { assertionType: 'url', operator: 'contains', value: '', timeout: 5000 },
    defaultName: 'Assert URL condition',
  },
  'assertion.element': {
    label: 'Assert Element',
    type: 'assertion',
    inputData: { assertionType: 'element', selector: '', exists: true, timeout: 5000 },
    defaultName: 'Assert element exists',
  },
  'assertion.text': {
    label: 'Assert Text',
    type: 'assertion',
    inputData: { assertionType: 'text', selector: '', operator: 'contains', value: '', timeout: 5000 },
    defaultName: 'Assert text content',
  },
  'control.runScript': {
    label: 'Run Script',
    type: 'control',
    inputData: { controlType: 'runScript', scriptId: '', scriptName: '', timeout: 60000 },
    defaultName: 'Run another script',
  },
  'verify.email': {
    label: 'Verify Email',
    type: 'verify',
    inputData: {
      verifyType: 'email',
      emailAddress: '{{email}}',
      subjectContains: '',
      appKey: '',
      timeout: 45000,
      onReceive: 'follow-link',
      storeAs: 'verificationLink',
    },
    defaultName: 'Verify email via Mailgun',
  },
  'ui.hide_by_text': {
    label: 'Hide by Text',
    type: 'ui',
    inputData: {
      uiType: 'hide_by_text',
      selector: '',
      textIncludes: [],
      textIncludesAny: [],
      textExcludes: [],
      excludeSelectors: [],
      caseSensitive: false,
      matchMode: 'hide',
      observeMutations: true,
      targetParent: false,
    },
    defaultName: 'Hide elements by text',
  },
  'dom.observe_mutation': {
    label: 'Observe DOM Mutation',
    type: 'dom',
    inputData: {
      domType: 'observe_mutation',
      target: '',
      childList: true,
      attributes: false,
      subtree: true,
      attributeFilter: [],
      debounceMs: 0,
      once: false,
      // The reaction is a nested step — today only ui.hide_by_text is
      // safe to re-fire mid-mutation. The editor surfaces it as a flat
      // form for the most common case (hide_by_text), and stores the
      // resulting nested step inside onMutation.step.
      onMutation: {
        type: 'execute_step',
        step: null,
      },
    },
    defaultName: 'Observe DOM for mutations',
  },
  'cookie.read': {
    label: 'Read Cookie',
    type: 'cookie',
    inputData: {
      cookieType: 'read',
      name: '',
      domain: '',
      storeAs: '',
      onMissing: 'continue',
    },
    defaultName: 'Read cookie value',
  },
  'tab.open_helper': {
    label: 'Open Helper Tab',
    type: 'tab',
    inputData: {
      tabType: 'open_helper',
      url: '',
      waitForLoad: true,
      waitForUrlMatches: '',
      closeAfterMs: 0,
      storeTabIdAs: '',
      timeoutMs: 30000,
    },
    defaultName: 'Open helper tab',
  },
  'event.listen_runtime': {
    label: 'Listen for Runtime Event',
    type: 'event',
    inputData: {
      eventType: 'listen_runtime',
      eventName: '',
      storeAs: '',
      onTimeout: 'continue',
      timeoutMs: 60000,
    },
    defaultName: 'Listen for chrome.runtime message',
  },
  'auth.sso_redirect_wait': {
    label: 'Wait for SSO Redirect',
    type: 'auth',
    inputData: {
      authType: 'sso_redirect_wait',
      untilUrlMatches: '',
      maxRedirects: 8,
      debounceMs: 250,
      timeoutMs: 30000,
    },
    defaultName: 'Wait for SSO chain to settle',
  },
  'network.intercept': {
    label: 'Intercept Network Request',
    type: 'network',
    inputData: {
      networkType: 'intercept',
      urlPattern: '',
      method: 'ANY',
      captureRequest: true,
      captureResponse: true,
      storeAs: '',
      onlyFirst: true,
      timeoutMs: 30000,
    },
    defaultName: 'Capture matching network request',
  },
  'network.modify': {
    label: 'Modify Network Request/Response',
    type: 'network',
    inputData: {
      networkType: 'modify',
      urlPattern: '',
      method: 'ANY',
      // rewriteRequestBody / rewriteResponseBody are objects:
      //   { mode: 'replace' | 'merge_json' | 'transform',
      //     template?: string, transformExpression?: string }
      rewriteRequestBody: null,
      rewriteResponseBody: null,
      captureRequest: true,
      captureResponse: true,
      storeAs: '',
      onlyFirst: true,
      timeoutMs: 30000,
    },
    defaultName: 'Modify matching network request body',
  },
};

// Flat list for dropdown rendering
export const STEP_TYPE_ITEMS = Object.entries(STEP_TEMPLATES).map(([key, tmpl]) => ({
  value: key,
  label: tmpl.label,
  type: tmpl.type,
}));

// ---------------------------------------------------------------------------
// Derive the composite key from a step object
// ---------------------------------------------------------------------------

export const getStepTypeKey = (step) => {
  if (!step) return 'action.navigate';
  // Canonical steps store a composite type directly (e.g. 'action.click', 'wait.selector').
  if (typeof step.type === 'string' && step.type.includes('.')) {
    return step.type;
  }
  if (step.type === 'action') {
    const action = step.inputData?.action || 'navigate';
    return `action.${action}`;
  }
  if (step.type === 'wait') {
    const wt = step.inputData?.waitType || 'selector';
    return `wait.${wt}`;
  }
  if (step.type === 'assertion') {
    const at = step.inputData?.assertionType || 'url';
    return `assertion.${at}`;
  }
  if (step.type === 'control') {
    const ct = step.inputData?.controlType || 'runScript';
    return `control.${ct}`;
  }
  if (step.type === 'verify') {
    const vt = step.inputData?.verifyType || 'email';
    return `verify.${vt}`;
  }
  if (step.type === 'ui') {
    const ut = step.inputData?.uiType || 'hide_by_text';
    return `ui.${ut}`;
  }
  if (step.type === 'dom') {
    const dt = step.inputData?.domType || 'observe_mutation';
    return `dom.${dt}`;
  }
  if (step.type === 'cookie') {
    const ct = step.inputData?.cookieType || 'read';
    return `cookie.${ct}`;
  }
  if (step.type === 'tab') {
    const tt = step.inputData?.tabType || 'open_helper';
    return `tab.${tt}`;
  }
  if (step.type === 'event') {
    const et = step.inputData?.eventType || 'listen_runtime';
    return `event.${et}`;
  }
  if (step.type === 'auth') {
    const at = step.inputData?.authType || 'sso_redirect_wait';
    return `auth.${at}`;
  }
  if (step.type === 'network') {
    const nt = step.inputData?.networkType || 'intercept';
    return `network.${nt}`;
  }
  return 'action.navigate';
};

// ---------------------------------------------------------------------------
// Colors per top-level type  (action / wait / assertion)
// ---------------------------------------------------------------------------

export const TYPE_COLORS = {
  action: { bg: '#e3f2fd', border: '#1976d2', text: '#1565c0', chip: 'primary' },
  wait:   { bg: '#fff8e1', border: '#f9a825', text: '#f57f17', chip: 'warning' },
  assertion: { bg: '#e8f5e9', border: '#2e7d32', text: '#1b5e20', chip: 'success' },
  control: { bg: '#f3e5f5', border: '#7b1fa2', text: '#6a1b9a', chip: 'secondary' },
  verify: { bg: '#e0f2f1', border: '#00897b', text: '#00695c', chip: 'info' },
  ui:     { bg: '#fff3e0', border: '#fb8c00', text: '#e65100', chip: 'warning' },
  dom:    { bg: '#ede7f6', border: '#673ab7', text: '#4527a0', chip: 'secondary' },
  cookie: { bg: '#fff8e1', border: '#fbc02d', text: '#f57f17', chip: 'warning' },
  tab:    { bg: '#e1f5fe', border: '#0288d1', text: '#01579b', chip: 'info' },
  event:  { bg: '#f1f8e9', border: '#558b2f', text: '#33691e', chip: 'success' },
  auth:   { bg: '#fce4ec', border: '#c2185b', text: '#880e4f', chip: 'error' },
  network:{ bg: '#e8eaf6', border: '#3949ab', text: '#283593', chip: 'primary' },
};

// Chip colors per fine-grained step type (used by the mockup-style rendering)
export const TYPE_CHIP_COLORS = {
  navigate:       { bg: '#e3f2fd', color: '#1565c0' },
  type:           { bg: '#e3f2fd', color: '#1565c0' },
  click:          { bg: '#e3f2fd', color: '#1565c0' },
  select:         { bg: '#e3f2fd', color: '#1565c0' },
  script:         { bg: '#f3e5f5', color: '#6a1b9a' },
  wait:           { bg: '#fff3e0', color: '#e65100' },
  wait_nav:       { bg: '#fff3e0', color: '#e65100' },
  assert:         { bg: '#e8f5e9', color: '#2e7d32' },
  assert_element: { bg: '#e8f5e9', color: '#2e7d32' },
  assert_text:    { bg: '#e8f5e9', color: '#2e7d32' },
  runScript:      { bg: '#f3e5f5', color: '#6a1b9a' },
};

// ---------------------------------------------------------------------------
// Failure handling options
// ---------------------------------------------------------------------------

export const FAILURE_OPTIONS = [
  { value: 'stop',       label: 'Stop execution' },
  { value: 'skip',       label: 'Skip this step and continue' },
  { value: 'jump',       label: 'Jump to step...' },
  { value: 'retry_stop', label: 'Retry N times then stop' },
  { value: 'retry_skip', label: 'Retry N times then skip' },
];

// ---------------------------------------------------------------------------
// Script-level type colors (login, progress, roster, etc.)
// ---------------------------------------------------------------------------

export const SCRIPT_TYPE_COLORS = {
  login:            { bg: '#e3f2fd', color: '#1565c0', label: 'Login' },
  progress:         { bg: '#e8f5e9', color: '#2e7d32', label: 'Progress' },
  roster:           { bg: '#fff3e0', color: '#e65100', label: 'Roster' },
  'de-roster':      { bg: '#fce4ec', color: '#c62828', label: 'De-roster' },
  'account-creation': { bg: '#f3e5f5', color: '#6a1b9a', label: 'Account Creation' },
  custom:           { bg: '#eceff1', color: '#455a64', label: 'Custom' },
};

// ---------------------------------------------------------------------------
// Assertion operators
// ---------------------------------------------------------------------------

export const ASSERTION_OPERATORS = [
  { value: 'contains',     label: 'contains' },
  { value: 'not_contains', label: 'not contains' },
  { value: 'equals',       label: 'equals' },
  { value: 'startsWith',   label: 'starts with' },
  { value: 'matches',      label: 'matches (regex)' },
];

// ---------------------------------------------------------------------------
// Helper: generate summary text for a step
// ---------------------------------------------------------------------------

export const getStepSummary = (step) => {
  const d = step?.inputData || {};
  switch (step?.type) {
    case 'action':
      switch (d.action) {
        case 'navigate':
          return d.url ? `Navigate to ${d.url}` : 'Navigate (no URL set)';
        case 'type':
          return `Type "${d.text || '...'}" into ${d.selector || '...'}`;
        case 'click':
          return `Click ${d.selector || '...'}`;
        case 'select':
          return `Select "${d.value || '...'}" in ${d.selector || '...'}`;
        case 'script':
          return d.script ? `Script: ${d.script.substring(0, 50)}...` : 'Execute script';
        default:
          return `Action: ${d.action || 'unknown'}`;
      }
    case 'wait':
      switch (d.waitType) {
        case 'selector':
          return `Wait for ${d.selector || '...'} (${d.timeout || 10000}ms)`;
        case 'navigation':
          return `Wait for navigation (${d.timeout || 15000}ms)`;
        case 'time':
          return `Wait ${d.timeout || 2000}ms`;
        default:
          return `Wait: ${d.waitType || 'unknown'}`;
      }
    case 'assertion':
      switch (d.assertionType) {
        case 'url':
          return `Assert URL ${(d.operator || 'contains').replace('_', ' ')} "${d.value || '...'}"`;
        case 'element':
          return `Assert ${d.selector || '...'} ${d.exists !== false ? 'exists' : 'does not exist'}`;
        case 'text':
          return `Assert text in ${d.selector || '...'} ${(d.operator || 'contains').replace('_', ' ')} "${d.value || '...'}"`;
        default:
          return `Assert: ${d.assertionType || 'unknown'}`;
      }
    case 'verify':
      if (d.verifyType === 'email') {
        const who = d.emailAddress || '{{email}}';
        const action = d.onReceive === 'follow-link' ? 'follow link'
                     : d.onReceive === 'extract-code' ? `extract code → {{${d.storeAs || 'verificationCode'}}}`
                     : d.onReceive === 'extract-link' ? `extract link → {{${d.storeAs || 'verificationLink'}}}`
                     : 'wait for delivery';
        return `Verify email at ${who} (${action})`;
      }
      return `Verify: ${d.verifyType || 'unknown'}`;
    case 'ui':
      if (d.uiType === 'hide_by_text') {
        const sel = d.selector || '...';
        const inc = Array.isArray(d.textIncludes) && d.textIncludes.length ? d.textIncludes.join(' + ') : '';
        const any = Array.isArray(d.textIncludesAny) && d.textIncludesAny.length ? d.textIncludesAny.join(' | ') : '';
        const text = inc && any ? `${inc} (${any})` : (inc || any || '(no text rule)');
        const mode = d.matchMode === 'remove' ? 'Remove' : 'Hide';
        return `${mode} ${sel} containing "${text}"`;
      }
      return `UI: ${d.uiType || 'unknown'}`;
    case 'dom':
      if (d.domType === 'observe_mutation') {
        const target = d.target || 'documentElement';
        const debounce = d.debounceMs > 0 ? `${d.debounceMs}ms` : 'rAF';
        const reactionType = d?.onMutation?.step?.type
          ? d.onMutation.step.type
          : (d?.onMutation?.steps?.length ? `${d.onMutation.steps.length} steps` : 'no reaction');
        return `Observe ${target} → ${reactionType} (debounce ${debounce}${d.once ? ', once' : ''})`;
      }
      return `DOM: ${d.domType || 'unknown'}`;
    case 'cookie':
      if (d.cookieType === 'read') {
        const dom = d.domain || 'current tab';
        return `Read cookie "${d.name || '...'}" from ${dom} → {{${d.storeAs || '...'}}}`;
      }
      return `Cookie: ${d.cookieType || 'unknown'}`;
    case 'tab':
      if (d.tabType === 'open_helper') {
        const wait = d.waitForUrlMatches ? `wait for ${d.waitForUrlMatches}` : (d.waitForLoad === false ? 'no wait' : 'wait for load');
        return `Open helper tab → ${d.url || '...'} (${wait})`;
      }
      return `Tab: ${d.tabType || 'unknown'}`;
    case 'event':
      if (d.eventType === 'listen_runtime') {
        return `Listen for "${d.eventName || '...'}" → {{${d.storeAs || '...'}}} (${(d.timeoutMs || 60000) / 1000}s)`;
      }
      return `Event: ${d.eventType || 'unknown'}`;
    case 'auth':
      if (d.authType === 'sso_redirect_wait') {
        return `Wait for SSO redirect to "${d.untilUrlMatches || '...'}" (max ${d.maxRedirects || 8} hops)`;
      }
      return `Auth: ${d.authType || 'unknown'}`;
    case 'network':
      if (d.networkType === 'intercept') {
        const m = d.method && d.method !== 'ANY' ? `${d.method} ` : '';
        return `Capture ${m}${d.urlPattern || '...'} → {{${d.storeAs || '...'}}}`;
      }
      return `Network: ${d.networkType || 'unknown'}`;
    default:
      return step?.name || 'Unknown step';
  }
};

// ---------------------------------------------------------------------------
// Helper: create a new step from a template key
// ---------------------------------------------------------------------------

export const createStepFromTemplate = (templateKey, stepNumber = 1) => {
  const tmpl = STEP_TEMPLATES[templateKey];
  if (!tmpl) return null;
  return {
    stepNumber,
    type: tmpl.type,
    name: tmpl.defaultName,
    inputData: { ...tmpl.inputData },
  };
};

// ---------------------------------------------------------------------------
// Helper: detect variables from steps  ({{varName}} patterns)
// ---------------------------------------------------------------------------

export const detectVariables = (steps) => {
  const varSet = new Set();
  const varPattern = /\{\{([^}]+)\}\}/g;
  (steps || []).forEach((step) => {
    const d = step.inputData || {};
    const fields = [d.url, d.text, d.selector, d.value, d.script, step.name];
    // ui.hide_by_text carries text matchers in arrays — flatten them in.
    if (d.uiType === 'hide_by_text') {
      ['textIncludes', 'textIncludesAny', 'textExcludes', 'excludeSelectors'].forEach((k) => {
        if (Array.isArray(d[k])) fields.push(...d[k]);
      });
    }
    // dom.observe_mutation carries the target selector + nested reaction.
    if (d.domType === 'observe_mutation') {
      fields.push(d.target);
      const nested = d?.onMutation?.step?.inputData;
      if (nested) {
        fields.push(nested.selector);
        ['textIncludes', 'textIncludesAny', 'textExcludes'].forEach((k) => {
          if (Array.isArray(nested[k])) fields.push(...nested[k]);
        });
      }
    }
    // cookie.read scans name + domain (storeAs becomes a NEW variable —
    // detectVariables intentionally does not declare it here, the user
    // sees it appear in the Variables panel after first execution).
    if (d.cookieType === 'read') {
      fields.push(d.name, d.domain);
    }
    if (d.tabType === 'open_helper') {
      fields.push(d.url, d.waitForUrlMatches);
    }
    if (d.eventType === 'listen_runtime') {
      fields.push(d.eventName);
    }
    if (d.authType === 'sso_redirect_wait') {
      fields.push(d.untilUrlMatches);
    }
    if (d.networkType === 'intercept') {
      fields.push(d.urlPattern);
    }
    fields.forEach((f) => {
      if (typeof f !== 'string') return;
      let match;
      while ((match = varPattern.exec(f)) !== null) {
        varSet.add(match[1].trim());
      }
    });
  });
  return Array.from(varSet);
};
